import type React from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/utils'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { PhotoEffectAdjustableRow } from './components/photo-effect-adjustable-row'
import { PhotoEffectToggleRow } from './components/photo-effect-toggle-row'
import { PhotoEffectsAdjustBar } from './components/photo-effects-adjust-bar'
import { PhotoEffectsHeader } from './components/photo-effects-header'
import { PhotoEffectsListViewport } from './components/photo-effects-list-viewport'
import { PhotoEffectsStatusBanner } from './components/photo-effects-status-banner'
import { FIGMA_SHADOW_MD, PHOTO_EFFECTS_Z_INDEX } from './constants/popover-layout'
import { usePhotoEffectsSession } from './hooks/use-photo-effects-session'
import { usePopoverPlacement } from './hooks/use-popover-placement'

export interface PhotoEffectsPopoverProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Selected photo shape — anchors initial placement near the canvas chrome. */
  shape: ShapeData
  scale: number
  canvasRef: React.RefObject<HTMLDivElement | null>
  /** Updates canvas preview + persists pezId on the shape (Step 10 / US-11). */
  onPezApplied?: (args: {
    shapeId: string
    pezId: string
    pezUrl: string
    historyTxnId?: string
  }) => void
}

/**
 * Photo Effects popover — catalog + apply + restore (Steps 9–11).
 */
export function PhotoEffectsPopover({
  open,
  onOpenChange,
  shape,
  scale,
  canvasRef,
  onPezApplied,
}: PhotoEffectsPopoverProps) {
  const {
    panelRef,
    pos,
    isDragging,
    isListResizingRef,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
  } = usePopoverPlacement({ open, shape, scale, canvasRef })

  const {
    catalogQuery,
    pezQuery,
    effectsState,
    activeCount,
    displayRows,
    isRestoring,
    catalogWarning,
    applyError,
    isApplying,
    handleToggle,
    handleValueChange,
    handleClearAll,
  } = usePhotoEffectsSession({ open, shape, onPezApplied })

  if (!open) return null

  const showBusy = catalogQuery.isLoading || isRestoring || isApplying

  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="false"
      aria-label="Photo Effects"
      aria-busy={showBusy || undefined}
      data-photo-effects-popover
      className={cn(
        'fixed box-border flex w-[340px] flex-col items-stretch bg-white',
        !pos && 'invisible pointer-events-none'
      )}
      style={{
        left: pos?.x ?? 0,
        top: pos?.y ?? 0,
        zIndex: PHOTO_EFFECTS_Z_INDEX,
        padding: 16,
        gap: 16,
        borderRadius: 8,
        border: '1px solid #E5E5E5',
        boxShadow: FIGMA_SHADOW_MD,
      }}
      onPointerDown={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex w-full flex-1 flex-col items-stretch" style={{ gap: 8 }}>
        <PhotoEffectsHeader
          isDragging={isDragging}
          showBusy={showBusy}
          onDragStart={handleDragStart}
          onDragMove={handleDragMove}
          onDragEnd={handleDragEnd}
          onClose={() => onOpenChange(false)}
        />

        <PhotoEffectsStatusBanner
          applyError={applyError}
          restoreFailed={pezQuery.isError}
          catalogWarning={catalogWarning}
        />

        <PhotoEffectsAdjustBar activeCount={activeCount} onClearAll={handleClearAll} />

        <PhotoEffectsListViewport
          onResizingChange={(resizing) => {
            isListResizingRef.current = resizing
          }}
          className={isApplying ? 'pointer-events-none opacity-70' : undefined}
        >
          {displayRows.map((def) =>
            def.kind === 'binary' ? (
              <PhotoEffectToggleRow
                key={def.id}
                label={def.label}
                active={effectsState[def.id]?.active ?? false}
                onToggle={() => handleToggle(def.id)}
              />
            ) : (
              <PhotoEffectAdjustableRow
                key={def.id}
                def={def}
                active={effectsState[def.id]?.active ?? false}
                value={effectsState[def.id]?.value ?? def.defaultValue ?? 0}
                onToggle={() => handleToggle(def.id)}
                onValueChange={(value) => handleValueChange(def.id, value)}
              />
            )
          )}
        </PhotoEffectsListViewport>
      </div>
    </div>,
    document.body
  )
}
